// src/components/desk/ClientRequestParts.jsx — the redesigned „კლიენტის მოთხოვნა" view (owner-approved
// direction 1b, Claude Design round 1, 2026-09-17).
//
// What the redesign is FOR: an agent grasps the client in five seconds and reaches the matches without
// scrolling. So the view reads top-down as
//   identity line (status · name · what they want · owner agent · team · lead alert)
//   → five-cell summary strip (budget · rooms · where · when · who lives there)
//   → must-have ✓ / nice-to-have ★ chips
//   → matches (MatchSuggestions, unchanged, now directly under the summary)
//   → tabs: criteria (grouped) · history · negotiations
// and the footer keeps the family rules shared with the property modal.
//
// ⚠️ Rules carried over, not reinvented:
//   * Unknown is printed „არ ვიცით", never a dash or a zero that reads like "no".
//   * A masked (another agent's) client announces what is hidden („დამალულია") and offers no team
//     actions; its representative's number is the one number shown.
//   * The intent is the DEMAND vocabulary (ქირაობა / ყიდვა / გირაო), never the listing one.
//   * Share lives in the footer only; the team popover still holds the full representative list,
//     share / leave / un-share, because those are the same panel (ClientSharePanel), not a copy.
import { useEffect, useRef, useState } from 'react'
import { t, tLog, getLanguage } from '../../lib/i18n.js'
import { intentLabel } from '../../lib/labels.js'
import { Icon } from '../Icon.jsx'

const CATEGORY_KEYS = { apartment: 'desk_category_opt_apartment', room: 'desk_category_opt_room', house: 'desk_category_opt_house', land: 'desk_category_opt_land', commercial: 'desk_category_opt_commercial', cottage: 'desk_category_opt_cottage', office: 'desk_category_opt_office', garage: 'desk_category_opt_garage', hotel: 'desk_category_opt_hotel', other: 'desk_category_opt_other' }
const URGENCY_KEYS = { immediate: 'desk_urgency_opt_immediate', soon: 'desk_urgency_opt_soon', flexible: 'desk_urgency_opt_flexible', browsing: 'desk_urgency_opt_browsing' }
const tKey = (map, v) => (v && map[v] ? t(map[v]) : v || null)

const RENTISH = new Set(['rent', 'lease', 'short_term_rent'])
const fmtNum = n => Number(n).toLocaleString('en-US')

// ── the pieces of the summary, each null when the request does not say ─────────────────────────
export function summaryOf(criteria, requirement, { districts = [], features = [], masked = false } = {}) {
  const c = criteria || {}
  const intent = requirement?.intent || null
  const ka = getLanguage() === 'ka'
  const dName = code => { const d = districts.find(x => x.code === code); return d ? ((ka ? d.name_ka : d.name_en) || d.name_en || d.name_ka || code) : code }
  const fName = code => { const f = features.find(x => x.code === code); return f ? ((ka ? f.name_ka : f.name_en) || f.name_ka || f.name_en || code) : code }

  const cur = c.currency || 'USD'
  const budget = (c.price_min == null && c.price_max == null) ? null : {
    main: c.price_min != null && c.price_max != null ? `${fmtNum(c.price_min)}–${fmtNum(c.price_max)}`
      : c.price_max != null ? `≤ ${fmtNum(c.price_max)}` : `≥ ${fmtNum(c.price_min)}`,
    unit: `${cur}${RENTISH.has(intent) ? ' ' + t('client_sum_per_month') : ''}`,
  }

  const range = (lo, hi) => lo == null && hi == null ? null : lo != null && hi != null ? (lo === hi ? `${lo}` : `${lo}–${hi}`) : hi != null ? `≤ ${hi}` : `${lo}+`
  const roomsMain = range(c.rooms_min, c.rooms_max)
  const beds = range(c.bedrooms_min, c.bedrooms_max)
  const rooms = roomsMain || beds ? { main: roomsMain || '—', unit: beds ? `· ${beds} ${t('client_sum_bedrooms_abbr')}` : '' } : null
  if (rooms && !roomsMain) { rooms.main = beds; rooms.unit = t('client_sum_bedrooms_abbr') }

  const codes = [...new Set([...(c.district_must_codes || []), ...(c.district_codes || [])])]
  let where = null
  if (codes.length) where = { main: dName(codes[0]), unit: codes.length > 1 ? tLog('client_sum_more', { n: String(codes.length - 1) }) : '' }
  else if (c.extra?.location_any_city) where = { main: t('client_sum_any_city'), unit: '' }
  else if (c.search_polygon) where = { main: t('client_sum_drawn_area'), unit: '' }

  const when = (() => {
    const parts = []
    if (c.move_in_date) parts.push(new Date(c.move_in_date).toLocaleDateString(ka ? 'ka-GE' : 'en-GB', { day: 'numeric', month: 'short' }))
    else if (requirement?.urgency) parts.push(tKey(URGENCY_KEYS, requirement.urgency))
    const lease = c.min_lease_months != null ? tLog('client_sum_lease', { n: String(c.min_lease_months) }) : null
    if (!parts.length && !lease) return null
    return { main: parts[0] || lease, unit: parts[0] && lease ? `· ${lease}` : '' }
  })()

  const household = (() => {
    if (masked) return { main: t('client_sum_hidden'), unit: '', hidden: true }
    const members = Array.isArray(c.extra?.household_members) ? c.extra.household_members : []
    const kids = members.filter(m => m && m.age != null && m.age < 18).length
    const adults = members.length - kids
    const parts = []
    if (adults) parts.push(tLog('client_sum_adults', { n: String(adults) }))
    if (kids) parts.push(tLog('client_sum_children', { n: String(kids) }))
    if (!parts.length && c.occupants != null) parts.push(tLog('client_sum_occupants', { n: String(c.occupants) }))
    const tags = (c.household_codes || []).map(fName)
    if (!parts.length && !tags.length) return null
    return { main: parts[0] || tags[0], unit: [...parts.slice(1), ...(parts.length ? tags : tags.slice(1))].map(x => `· ${x}`).join(' ') }
  })()

  return {
    intentLine: [intent ? intentLabel(intent, 'demand') : null, tKey(CATEGORY_KEYS, requirement?.property_category),
      requirement?.urgency ? tKey(URGENCY_KEYS, requirement.urgency) : null].filter(Boolean).join(' · '),
    budget, rooms, where, when, household,
    must: (c.must_have_codes || []).map(fName),
    nice: (c.nice_to_have_codes || []).map(fName),
  }
}

function Cell({ label, value, className = '' }) {
  return (
    <div className={`bg-card px-3.5 py-2.5 min-w-0 ${className}`} data-sum-cell={label}>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      {value ? (
        <p className={`leading-snug break-words ${value.hidden ? 'text-sm text-muted-foreground italic' : 'text-[15px] font-semibold tabular-nums'}`}>
          {value.main}{value.unit ? <span className="text-[13px] font-normal text-muted-foreground"> {value.unit}</span> : null}
        </p>
      ) : (
        // „არ ვიცით" — an unanswered field is not a "no".
        <p className="text-sm text-muted-foreground" data-unknown>{t('client_sum_unknown')}</p>
      )}
    </div>
  )
}

export function ClientSummaryStrip({ summary }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border/60 border border-border rounded-xl overflow-hidden" data-client-summary>
      <Cell label={t('client_sum_budget')} value={summary.budget} />
      <Cell label={t('client_sum_rooms')} value={summary.rooms} />
      <Cell label={t('client_sum_where')} value={summary.where} />
      <Cell label={t('client_sum_when')} value={summary.when} />
      {/* Five cells in a two-column phone grid: the last one spans, so no empty grey slot is left. */}
      <Cell label={t('client_sum_household')} value={summary.household} className="col-span-2 lg:col-span-1" />
    </div>
  )
}

export function ClientFeatureChips({ summary }) {
  if (!summary.must.length && !summary.nice.length) return null
  const chip = 'inline-flex items-center h-7 px-2.5 rounded-full border text-xs whitespace-nowrap'
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-client-features>
      {summary.must.length > 0 && <>
        <span className="text-[13px] font-medium text-muted-foreground mr-0.5">{t('client_must')}</span>
        {summary.must.map(n => <span key={'m' + n} className={`${chip} border-emerald-500/45 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300`}>✓ {n}</span>)}
      </>}
      {summary.must.length > 0 && summary.nice.length > 0 && <span className="w-px h-5 bg-border mx-1" />}
      {summary.nice.length > 0 && <>
        <span className="text-[13px] font-medium text-muted-foreground mr-0.5">{t('client_nice')}</span>
        {summary.nice.map(n => <span key={'n' + n} className={`${chip} border-border text-foreground/80`}>★ {n}</span>)}
      </>}
    </div>
  )
}

// „მე + 14 კოლეგა ⌄" — the whole representative panel behind one chip. `openSignal` (the footer share
// pill's counter) opens the popover AND the picker inside it, so the footer stays the one share door.
// `children` is a function of the picker signal: the share pill's counter reaches the panel only when the
// pill opened the popover — reopening it by hand later must not spring the picker again.
export function ClientTeamChip({ representatives = [], myAgentId = null, openSignal = 0, children }) {
  const [open, setOpen] = useState(false)
  const [bySignal, setBySignal] = useState(false)
  const ref = useRef(null)
  useEffect(() => { if (openSignal) { setOpen(true); setBySignal(true) } }, [openSignal])
  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey, true) }
  }, [open])
  const mine = representatives.some(r => r.agent_id && r.agent_id === myAgentId)
  const others = representatives.length - (mine ? 1 : 0)
  const label = !representatives.length ? t('client_team_pool')
    : mine ? (others ? tLog('client_team_me_plus', { n: String(others) }) : t('client_team_me'))
    : (others > 1 ? tLog('client_team_name_plus', { name: representatives[0].name || '—', n: String(representatives.length - 1) }) : (representatives[0].name || '—'))
  return (
    <span className="relative" ref={ref}>
      <button type="button" onClick={() => { setBySignal(false); setOpen(o => !o) }} aria-expanded={open} data-team-chip
        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs whitespace-nowrap ${mine
          ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
          : !representatives.length ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400' : 'border-border text-foreground/85'}`}>
        <Icon name="user" size={12} /> {label} <span aria-hidden>⌄</span>
      </button>
      {/* Below sm the popover pins to the screen's side margins (anchored to the chip it ran 9px off the
          left edge at 375px); from sm up it hangs under the chip. */}
      {open && (
        <div className="fixed inset-x-4 top-24 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full mt-1 z-30 sm:w-[420px] max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-card shadow-xl p-3" data-team-popover>
          {typeof children === 'function' ? children(bySignal ? openSignal : 0) : children}
        </div>
      )}
    </span>
  )
}

export function MaskedClientBanner({ agent, children }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/20 px-3.5 py-2.5 space-y-1.5" data-masked-banner>
      {agent?.name && (
        <p className="text-sm">
          {tLog('client_masked_rep', { name: agent.name })}
          {agent.phone && <> · <a href={`tel:${agent.phone}`} className="text-primary tabular-nums">{agent.phone}</a></>}
        </p>
      )}
      <p className="text-xs text-muted-foreground">{t('client_masked_note')}</p>
      {children}
    </div>
  )
}

// Criteria · History · Negotiations. Each tab's body may render NOTHING (RunningNote and the
// negotiations panel are silent when empty) — `only:block` shows the empty line exactly then.
export function ClientTabs({ criteria, history, negotiations }) {
  const [tab, setTab] = useState('criteria')
  const tabs = [['criteria', t('client_tab_criteria')], ['history', t('client_tab_history')], ['nego', t('client_tab_nego')]]
  const body = tab === 'criteria' ? criteria : tab === 'history' ? history : negotiations
  return (
    <div data-client-tabs>
      <div className="flex border-b border-border overflow-x-auto overflow-y-hidden no-scrollbar" style={{ scrollbarWidth: 'none' }} role="tablist">
        {tabs.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-client-tab={id}
            className={`px-3.5 py-2 text-[13px] whitespace-nowrap border-b-2 -mb-px ${tab === id ? 'border-primary text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="pt-3">
        {body}
        <p className="hidden only:block text-sm text-muted-foreground py-2">{t('client_tab_empty')}</p>
      </div>
    </div>
  )
}
